import { useEffect } from 'react';
import { useAppStore } from '../store/useAppStore';
import { apiUrl } from '../services/api';
import { stateService, type GlobalReaderState } from '../services/stateService';
import { useSmartSync } from './useSmartSync';
import { pdfManagerService } from '../services/pdfManagerService';

const selectGlobalState = (): GlobalReaderState => {
  const state = useAppStore.getState();
  return { favorites: state.favorites, favoriteLists: state.favoriteLists, bookmarks: state.bookmarks, hiddenIds: state.hiddenIds, settings: state.settings, activeId: state.activeId, sort: state.sort };
};

const serializeGlobalState = () => JSON.stringify(selectGlobalState());

export function useGlobalStateSync() {
  useSmartSync();
  useEffect(() => {
    let hydrated = false;
    let applyingRemote = false;
    let disposed = false;
    let saveInFlight = false;
    let timer: number | undefined;
    let localRevision = 0;
    let savedRevision = 0;
    let lastSerialized = serializeGlobalState();
    const openedAtById = new Map(useAppStore.getState().items.map((item) => [item.id, item.lastOpenedAt]));

    const scheduleSave = (delay = 50) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => { void flush(); }, delay);
    };

    const flush = async () => {
      if (!hydrated || disposed || saveInFlight) return;
      window.clearTimeout(timer);
      timer = undefined;
      const revision = localRevision;
      const snapshot = selectGlobalState();
      const serialized = JSON.stringify(snapshot);
      saveInFlight = true;
      try {
        await stateService.save(snapshot);
        if (!disposed) {
          savedRevision = Math.max(savedRevision, revision);
          lastSerialized = serialized;
        }
      } catch (error) {
        console.error('Unable to save global reader state:', error);
        if (!disposed) scheduleSave(2000);
      } finally {
        saveInFlight = false;
        if (!disposed && localRevision > revision) scheduleSave();
      }
    };

    const hydrate = async () => {
      try {
        const remote = await stateService.get();
        if (remote.state) {
          applyingRemote = true;
          useAppStore.getState().hydrateGlobalState(remote.state);
          applyingRemote = false;
          lastSerialized = serializeGlobalState();
        } else {
          await stateService.save(selectGlobalState());
        }
      } finally {
        hydrated = true;
      }
    };
    void hydrate();

    const unsubscribe = useAppStore.subscribe(() => {
      const activeId = useAppStore.getState().activeId;
      const activeItem = useAppStore.getState().items.find((item) => item.id === activeId);
      if (activeId && activeItem && !openedAtById.has(activeId)) {
        for (const item of useAppStore.getState().items) openedAtById.set(item.id, item.lastOpenedAt);
      } else if (activeId && activeItem?.lastOpenedAt !== openedAtById.get(activeId)) {
        openedAtById.set(activeId, activeItem?.lastOpenedAt);
        void pdfManagerService.opened(activeId).catch(() => undefined);
      }
      if (!hydrated || applyingRemote) return;
      const serialized = serializeGlobalState();
      if (serialized === lastSerialized) return;
      lastSerialized = serialized;
      localRevision++;
      scheduleSave();
    });

    const events = new EventSource(apiUrl('/events'), { withCredentials: true });
    events.addEventListener('state', () => {
      if (document.visibilityState !== 'visible' || !hydrated || applyingRemote || saveInFlight || timer !== undefined || localRevision > savedRevision) return;
      const revision = localRevision;
      void stateService.get().then((remote) => {
        if (!remote.state || disposed || revision !== localRevision || saveInFlight || timer !== undefined || localRevision > savedRevision) return;
        applyingRemote = true;
        const activeId = useAppStore.getState().activeId;
        useAppStore.getState().hydrateGlobalState({ ...remote.state, activeId });
        applyingRemote = false;
        lastSerialized = serializeGlobalState();
      }).catch((error) => console.error('Unable to refresh global reader state:', error));
    });

    const saveBeforeExit = () => {
      if (!hydrated || localRevision <= savedRevision) return;
      void stateService.saveKeepalive(selectGlobalState()).catch(() => undefined);
    };
    window.addEventListener('pagehide', saveBeforeExit);

    return () => {
      saveBeforeExit();
      disposed = true;
      unsubscribe();
      events.close();
      window.removeEventListener('pagehide', saveBeforeExit);
      window.clearTimeout(timer);
    };
  }, []);
}
